/* eslint-disable import/prefer-default-export */
import { useCallback, useEffect, useRef, useState } from 'react';
import { isPlainObject, isString } from 'lodash';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';

import {
  fetchNonRoutableRequest,
  refreshNonRoutableRequestsIfActive,
} from 'store/thunks/workQueueThunks';

import { describeNonRoutableActionError } from '../utils/nonRoutableRequestErrors';

/**
 * Runs one irreversible resolution (Bring Back to POM, or Cancel) and turns its result into
 * something a human can act on. Shared by both dialogs so the two can never drift apart on the
 * only thing that matters here — what the operator is told happened.
 *
 * Both actions drive the row to a TERMINAL status. Whether it then LEAVES THE LIST depends on the
 * operator's Status filter: the DEFAULT view serves PENDING | RESOLUTION_IN_PROGRESS, so the row
 * normally vanishes — correct behaviour, not a failure, and the success messages say so, because a
 * row disappearing from a queue of stuck prescriptions must not read as something gone wrong. But
 * Cancelled is a filterable status, so with it ticked a cancelled row REMAINS after a successful
 * cancel. Neither dialog may promise the row will disappear.
 *
 * `open` IS REQUIRED, and the reason is a real defect this had. The row menu mounts both dialogs
 * for the lifetime of the row, so closing one unmounts nothing and this hook's state survives.
 * Without the reset below, an operator who hit QUEUE_UNAVAILABLE, waited for engineering to fix
 * the stack and reopened the same row found the stale red alert still there and NO confirm button
 * — the parked prescription could not be released without navigating away to force a remount, and
 * the alert read as the result of an attempt that had never happened.
 *
 * THE QUEUE IS REFRESHED WHEN THE DIALOG CLOSES, NOT WHEN THE MUTATION RETURNS. This looks like an
 * odd place for a refetch, so it needs its reason recorded.
 *
 * `NonRoutableRequestsQueueOptions` — and therefore both dialogs — is rendered PER ROW. Refreshing
 * as soon as the mutation settled dropped the resolved row from the page, which unmounted the row,
 * the menu and the open dialog together, taking the outcome message with them. The messages that
 * vanished are the ones that matter most: RELEASED_READ_BACK_FAILED and
 * CANCELLED_READ_BACK_FAILED both say the irreversible thing HAS happened, and NOT_PENDING says
 * somebody else already resolved it.
 *
 * IT IS NOT ENOUGH THAT SOME OF THEM SURVIVE TODAY. RESOLUTION_NOT_RECORDED — the single most
 * dangerous message this screen can show — happens to survive only because it leaves the row in
 * RESOLUTION_IN_PROGRESS, which is inside the queue's PENDING | RESOLUTION_IN_PROGRESS allow-list.
 * That is accidental correctness: it would break the day somebody narrows the queue filter, and
 * nothing in that future change would look related. So the safeguard must not depend on WHICH
 * statuses the queue serves, and deferring the refresh is what makes it independent of them.
 *
 * POM-6279 — THE FAX ACTIONS USE THIS SAME HOOK. Their thunks resolve `{ detail, result }`, where
 * `detail` is the RelayFaxActionResult's `request` and `result` is the whole result (`outcome`,
 * `faxMayHaveBeenSent`). Three additions make that work without a second hook drifting from this
 * one:
 *   - `submit(args)` passes `args` to the action (Resend's `confirmResend: true`). A click event is
 *     NOT args — only a plain object is forwarded, so `onClick={submit}` stays safe.
 *   - `successMessage(detail, result)` may return `{ message, variant }` instead of a string, so a
 *     Retry that parked the request again can say so as `info` rather than as a success.
 *   - `holdOpen(detail, result)`: when true the dialog does NOT close on success and `completed`
 *     carries the result instead — for Cancel fax when the fax may already be at the pharmacy, where
 *     the instruction to phone them must stay on screen until the operator dismisses it.
 */
const toSnackbar = (success) =>
  isString(success) ? { message: success, variant: 'success' } : { variant: 'success', ...success };

export const useNonRoutableResolution = ({
  item,
  action,
  successMessage,
  onClose,
  open,
  holdOpen = undefined,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  // { described, statusNow } once an attempt has come back with anything other than a clean
  // success. Kept in the dialog rather than thrown at the global error modal, because these codes
  // carry opposite instructions and the modal would flatten them into one message.
  const [outcome, setOutcome] = useState(null);
  // `{ detail, result }` of a success the dialog was told to hold open for (see `holdOpen`).
  const [completed, setCompleted] = useState(null);
  // Whether anything was actually sent, so closing a dialog the operator merely looked at does
  // not fire a pointless read.
  const attempted = useRef(false);

  // Clear on OPEN rather than on close: a dialog that wipes its own alert as it closes would take
  // the message away at the moment the operator dismissed it, and some of these messages
  // ("the request WAS released") are the only place that fact appears.
  useEffect(() => {
    if (open) {
      setOutcome(null);
      setCompleted(null);
      attempted.current = false;
    }
  }, [open]);

  /**
   * Close the dialog and, if anything was sent, refresh the queue.
   *
   * Every dismissal path goes through here — the button, Escape, a clean success — so a resolved
   * row cannot linger in the list once its dialog is gone. `refreshNonRoutableRequestsIfActive`
   * keeps its own guard, so this is still a no-op when the operator has since switched tabs.
   */
  const dismiss = useCallback(() => {
    if (attempted.current) {
      attempted.current = false;
      dispatch(refreshNonRoutableRequestsIfActive());
    }
    onClose();
  }, [dispatch, onClose]);

  const submit = useCallback(
    async (args) => {
      setSubmitting(true);
      setOutcome(null);
      attempted.current = true;

      try {
        const { detail, result, error } = await dispatch(
          action({
            ...(isPlainObject(args) ? args : {}),
            nonRoutableRequestId: item.nonRoutableRequestId,
          })
        );

        if (detail) {
          // The success message is a notistack snackbar, rendered by the provider at the app root
          // (index.js), so it survives this row being dropped by the refresh `dismiss` triggers.
          const { message, variant } = toSnackbar(successMessage(detail, result));
          enqueueSnackbar(message, { variant });

          if (holdOpen?.(detail, result)) {
            setCompleted({ detail, result });
            return;
          }

          dismiss();
          return;
        }

        const described = describeNonRoutableActionError(error);

        // NOT_PENDING: the operator's very next question is always "then what happened to it?", and
        // "already released" and "cancelled" are not equivalent — one of them filled the
        // prescription. The by-id read serves any status, so ask it rather than guessing. A failed
        // read resolves undefined and the alert falls back to its generic wording.
        //
        // The WHOLE row is kept, not just the status: `resolutionType`, `resolvedAt` and
        // `resolvedByPomUserId` are the rest of that answer, and this is the one screen that can
        // give it — the queue itself cannot serve a terminal row at all.
        let resolvedRequest;
        if (described.code === 'NON_ROUTABLE_REQUEST_NOT_PENDING') {
          resolvedRequest = await dispatch(
            fetchNonRoutableRequest({ nonRoutableRequestId: item.nonRoutableRequestId })
          );
        }

        setOutcome({ described, resolvedRequest });
      } finally {
        setSubmitting(false);
      }
    },
    [
      dispatch,
      action,
      item.nonRoutableRequestId,
      successMessage,
      holdOpen,
      dismiss,
      enqueueSnackbar,
    ]
  );

  return { submit, submitting, outcome, completed, dismiss };
};
